import React, { useMemo } from 'react';
import { Twitch, Twitter, Instagram, Youtube, ArrowUp } from 'lucide-react';

// Custom TikTok Icon Component
const TikTokIcon = ({ size = 18, className }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export default function Footer() {
  // Memoize navigation items
  const navLinks = useMemo(() => [
    { label: 'Home', href: '/' },
    { label: 'Creators', href: '/creators' },
    { label: 'Gallery', href: '/gallery' },
    { label: 'Apply', href: '/apply' },
    { label: 'Contact', href: '#!' }
  ], []);

  // FIXED: Changed href from '#' to '#!' to satisfy the valid-anchor linter rule
  const socialLinks = useMemo(() => [
    { Icon: Twitch, label: 'Twitch', href: 'https://twitch.tv/gamemeetsworld' },
    { Icon: Twitter, label: 'Twitter', href: 'https://x.com/gamemeetsworld' },
    { Icon: Instagram, label: 'Instagram', href: 'https://instagram.com/gamemeetsworld' },
    { Icon: TikTokIcon, label: 'TikTok', href: 'https://tiktok.com/@gamemeetsworld' },
    { Icon: Youtube, label: 'YouTube', href: 'https://youtube.com/@gamemeetsworld' }
  ], []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#021206] pt-20 pb-10 px-6 relative overflow-hidden border-t border-white/5">
      
      {/* Decorative Grid Background */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none" 
        style={{ 
          backgroundImage: 'linear-gradient(rgba(161, 255, 79, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(161, 255, 79, 0.05) 1px, transparent 1px)', 
          backgroundSize: '40px 40px' 
        }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Top Section: Logo & Nav */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-16 gap-10 lg:gap-6">
          
          {/* Logo Area - Text Only */}
          <div className="flex flex-col">
            <div className="text-2xl sm:text-3xl font-black tracking-tighter uppercase leading-none">
              <span className="text-[#A1FF4F]">Game</span>
              <span className="text-white">Meets</span>
              <span className="text-[#A1FF4F]">World</span>
            </div>
            <span className="text-[10px] text-white/60 tracking-[0.3em] font-mono uppercase mt-2">
              Powered by OrangeLabX
            </span>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-wrap gap-4 sm:gap-6 lg:gap-10 w-full lg:w-auto" aria-label="Footer navigation">
            {navLinks.map((item) => (
              <a 
                key={item.label} 
                href={item.href} 
                className="text-white/70 hover:text-[#A1FF4F] font-bold uppercase tracking-wider text-xs sm:text-sm transition-colors relative group focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206] rounded py-1"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#A1FF4F] transition-all group-hover:w-full" aria-hidden="true" />
              </a>
            ))}
          </nav>

          {/* Social Icons */}
          <div className="flex gap-3 flex-wrap" role="list" aria-label="Social media links">
            {socialLinks.map((social) => (
              <a 
                key={social.label} 
                href={social.href} 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 sm:w-11 sm:h-11 bg-[#0F1419] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#021206] hover:bg-[#A1FF4F] hover:border-[#A1FF4F] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206]"
                aria-label={`Visit our ${social.label} page`}
              >
                <social.Icon size={18} />
              </a>
            ))}
          </div>
        </div>
        
        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-[#A1FF4F]/30 to-transparent mb-10" aria-hidden="true" />

        {/* Bottom Section: Copyright & Legal */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-white/60 font-mono uppercase tracking-wide">
          <p className="text-center md:text-left">
            © 2025 GameMeetsWorld. All rights reserved.
          </p>
          
          <nav className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-center" aria-label="Legal information">
            {/* FIXED: Changed href to '#!' */}
            <a 
              href="#!" 
              className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206] rounded px-2 py-1"
            >
              Privacy Policy
            </a>
            <a 
              href="#!" 
              className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206] rounded px-2 py-1"
            >
              Terms of Service
            </a>
            <a 
              href="#!" 
              className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206] rounded px-2 py-1"
            >
              Cookies
            </a>
          </nav>

          {/* Scroll to Top Button */}
          <button 
            onClick={scrollToTop}
            className="group flex items-center gap-2 text-[#A1FF4F] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206] rounded px-2 py-1"
            aria-label="Scroll back to top of page"
          >
            <span className="text-xs sm:text-sm">Back to Top</span>
            <div className="w-6 h-6 sm:w-7 sm:h-7 border border-[#A1FF4F] flex items-center justify-center group-hover:bg-[#A1FF4F] group-hover:text-black transition-all">
               <ArrowUp size={14} aria-hidden="true" />
            </div>
          </button>
        </div>

      </div>
    </footer>
  );
}
